import { useState, useCallback } from 'react'
import axios from 'axios'

import { parseErrorToUserReadableMessage } from 'utils/errors'

export type useApiParams<D, P> = P extends undefined ? (params?: P) => Promise<D> : (params: P) => Promise<D>

export type RequestResult<D, P> = {
  data: D | undefined
  isDone: boolean
  isError: boolean
  isLoading: boolean
  errorMessage: string
  clearData: VoidFunction
  call: (params?: P | undefined) => Promise<void | D>
}

export function useRequest<D, P = undefined>(callback: useApiParams<D, P>): RequestResult<D, P> {
  const [isDone, setIsDone] = useState(false)
  const [isError, setIsError] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const [data, setData] = useState<D>()
  const [errorMessage, setErrorMessage] = useState('')

  const getData = async (params?: P) => {
    setIsDone(false)
    setIsLoading(true)
    setIsError(false)
    setErrorMessage('')
    try {
      // @ts-ignore
      const data = await callback(params)
      setIsLoading(false)

      setIsDone(true)

      setData(data)

      return data
    } catch (error) {
      if (!axios.isCancel(error)) {
        setIsError(true)
        const humanFormat = parseErrorToUserReadableMessage(error)
        setErrorMessage(humanFormat)
        setIsLoading(false)
      }
      return undefined
    }
  }

  const call = async (params?: P) => {
    if (!isLoading) {
      return await getData(params)
    }

    return undefined
  }

  const clearData = useCallback(() => {
    setData(undefined)
    setIsError(false)
    setIsDone(false)
    setIsLoading(false)
  }, [setData, setIsError, setIsDone, setIsLoading])

  return {
    data,
    call,
    isDone,
    isError,
    clearData,
    isLoading,
    errorMessage,
  }
}
